import { FC, useState } from "react";
import { ArrowClockwise } from "@phosphor-icons/react";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import Typhography from "../../../shared/Typhography/Typhography";
import styles from "./PostMatchTab.module.scss";

interface MinutesEntry {
  athleteId: string;
  name?: string;
  minutes?: number;
  percentage?: number;
  source?: string;
}

interface PostMatchTabProps {
  matchResult?: {
    homeScore?: number;
    awayScore?: number;
    outcome?: string;
  };
  minutesPlayed: MinutesEntry[];
  postMatchNotes?: string;
  totalMinutes?: number;
  sport?: string;
  onSave: (data: {
    result?: { homeScore?: number; awayScore?: number; outcome?: string };
    postMatchNotes?: string;
    matchStats?: Record<string, number>;
  }) => Promise<void>;
  onRecalc: (totalMinutes: number) => Promise<void>;
  isLoading?: boolean;
}

const OUTCOME_LABELS: Record<string, string> = {
  W: "Vittoria",
  D: "Pareggio",
  L: "Sconfitta",
};

/**
 * Tab POST-GARA — risultato + minutesPlayed + note + stats sport-specifiche.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 742-773.
 */
const PostMatchTab: FC<PostMatchTabProps> = ({
  matchResult,
  minutesPlayed,
  postMatchNotes,
  totalMinutes = 90,
  sport = "soccer",
  onSave,
  onRecalc,
  isLoading = false,
}) => {
  const [homeScore, setHomeScore] = useState<string>(
    matchResult?.homeScore?.toString() ?? ""
  );
  const [awayScore, setAwayScore] = useState<string>(
    matchResult?.awayScore?.toString() ?? ""
  );
  const [outcome, setOutcome] = useState<string>(matchResult?.outcome ?? "");
  const [notes, setNotes] = useState(postMatchNotes ?? "");
  const [saves, setSaves] = useState(false);

  const handleSave = async () => {
    setSaves(true);
    try {
      await onSave({
        result: {
          homeScore: homeScore ? parseInt(homeScore, 10) : undefined,
          awayScore: awayScore ? parseInt(awayScore, 10) : undefined,
          outcome: outcome || undefined,
        },
        postMatchNotes: notes || undefined,
      });
    } finally {
      setSaves(false);
    }
  };

  const handleRecalc = async () => {
    await onRecalc(totalMinutes);
  };

  return (
    <div className={styles.wrap}>
      {/* Risultato */}
      <section className={styles.section}>
        <Typhography size="bold-14" className="mb-3">
          Risultato
        </Typhography>
        <div className={styles.scoreRow}>
          <input
            type="number"
            className={styles.scoreInput}
            placeholder="Casa"
            value={homeScore}
            onChange={(e) => setHomeScore(e.target.value)}
            min={0}
          />
          <span className={styles.scoreSep}>-</span>
          <input
            type="number"
            className={styles.scoreInput}
            placeholder="Ospite"
            value={awayScore}
            onChange={(e) => setAwayScore(e.target.value)}
            min={0}
          />
          <select
            className={styles.outcomeSelect}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
          >
            <option value="">Esito...</option>
            {Object.entries(OUTCOME_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </section>

      {/* Minuti giocati */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <Typhography size="bold-14">Minuti giocati</Typhography>
          <CustomButton
            size="sm"
            color="outline"
            onClick={handleRecalc}
            disabled={isLoading}
          >
            <ArrowClockwise size={12} />
            Ricalcola da cambi
          </CustomButton>
        </div>

        {minutesPlayed.length > 0 ? (
          <div className={styles.minutesList}>
            {minutesPlayed.map((entry) => (
              <div key={entry.athleteId} className={styles.minuteRow}>
                <span className={styles.playerName}>
                  {entry.name ?? entry.athleteId}
                </span>
                <div className={styles.minuteBar}>
                  <div
                    className={styles.minuteBarFill}
                    style={{ width: `${entry.percentage ?? 0}%` }}
                  />
                </div>
                <span className={styles.minuteValue}>
                  {entry.minutes ?? 0}' ({entry.percentage ?? 0}%)
                </span>
                {entry.source === "auto_from_subs" && (
                  <span className={styles.sourceTag}>auto</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.emptyText}>
            Nessun dato. Usa "Ricalcola da cambi" o inserisci manualmente.
          </p>
        )}
      </section>

      {/* Note partita */}
      <section className={styles.section}>
        <Typhography size="bold-14" className="mb-2">
          Note partita
        </Typhography>
        <textarea
          className={styles.notesArea}
          placeholder={`Cosa ha funzionato, cosa migliorare...`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
        />
      </section>

      <div className={styles.actions}>
        <CustomButton
          color="primary"
          onClick={handleSave}
          disabled={saves || isLoading}
        >
          {saves ? "Salvataggio..." : "Salva post-gara"}
        </CustomButton>
      </div>
    </div>
  );
};

export default PostMatchTab;
